import React from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import BarChart from '../charts/BarChart';
import StackedBarChart from '../charts/StackedBarChart';
import DataTable from '../DataTable';
import { formatCurrency, formatInt, formatPct, formatUnitPrice } from '../../utils/format';
import { riskNext } from '../../utils/riskDisplay';
import {
  openDrillSelection,
  selectionFromMonthMark,
  selectionFromWellMark,
} from '../../utils/drilldown';
import { useDeal } from '../../deals/DealContext';
import { isOilPrimary, shouldShowGasVolumes } from '../../utils/commodityDisplay';

export default function Executive({ data, onNavigate }) {
  const { deal } = useDeal();
  const { kpis, topWells, projectedCf, reservesSummary, risks, concentration } = data;
  const dealOrMeta = deal || data?.meta;
  const isOil = isOilPrimary(dealOrMeta);
  const showGasVol = shouldShowGasVolumes(dealOrMeta, reservesSummary, ['netMmcf', 'netGasMmcf']);

  const drillWell = (d) => openDrillSelection(selectionFromWellMark(d), { path: 'executive' });
  const drillPeriod = (d) => openDrillSelection(selectionFromMonthMark(d, 'year'), { path: 'executive' });

  const subtitle = isOil
    ? `${deal?.subtitle || deal?.label || 'Oil package'} · ${formatCurrency(
        kpis.pdpPv10MM != null ? kpis.pdpPv10MM * 1e6 : null,
      )} PDP PV10`
    : `East TX operated gas · ${formatCurrency(kpis.pdpPv10MM * 1e6)} PDP PV10 · ${formatCurrency(
        kpis.ntmPdpCfMM != null ? kpis.ntmPdpCfMM * 1e6 : null,
      )} NTM cash flow · bids Aug 5`;

  const avgWiNri =
    kpis.avgWi != null && kpis.avgNri != null ? `${kpis.avgWi}% / ${kpis.avgNri}%` : '—';

  const reservesVolumeColumns = [];
  if (isOil) {
    reservesVolumeColumns.push({
      key: 'netOilMbbl',
      label: 'Net oil (Mbbl)',
      format: formatInt,
    });
  }
  if (showGasVol) {
    reservesVolumeColumns.push({
      key: 'netMmcf',
      label: 'Net gas (MMcf)',
      format: formatInt,
    });
  }
  if (!reservesVolumeColumns.length) {
    reservesVolumeColumns.push({
      key: 'netMmcf',
      label: 'Net gas (MMcf)',
      format: formatInt,
    });
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Executive Overview</h2>
          <p className="page-subtitle">{subtitle}</p>
          <div className="page-quote">
            <span className="page-quote-label">{data.quoteLabel}</span>
            <span>&ldquo;{data.quote}&rdquo;</span>
          </div>
        </div>
      </div>

      <KPIGrid>
        <KPICard
          label="PDP PV10"
          value={formatCurrency(kpis.pdpPv10MM != null ? kpis.pdpPv10MM * 1e6 : null)}
          sub={`Total (PDP+PBP) ${formatCurrency(kpis.totalPv10MM != null ? kpis.totalPv10MM * 1e6 : null)}`}
          color="var(--amber)"
        />
        <KPICard
          label="NTM PDP cash flow"
          value={formatCurrency(kpis.ntmPdpCfMM != null ? kpis.ntmPdpCfMM * 1e6 : null)}
          sub={`L12M NOCF ${formatCurrency(kpis.l12NocfMM != null ? kpis.l12NocfMM * 1e6 : null)}`}
          color="var(--green)"
        />
        <KPICard
          label={isOil ? 'Net production' : 'Aug ’26 net production'}
          value={
            kpis.augProdMcfpd != null
              ? `${formatInt(kpis.augProdMcfpd)} Mcfpd`
              : '—'
          }
          sub={isOil ? 'Oil-primary package' : '100% gas · 32 PDP wells'}
          color="var(--blue)"
        />
        <KPICard
          label="Net acres"
          value={kpis.netAcres != null ? formatInt(Math.round(kpis.netAcres)) : '—'}
          sub={kpis.hbpPct != null ? `${formatPct(kpis.hbpPct * 100)} HBP` : '—'}
          color="var(--navy)"
        />
        <KPICard
          label="Avg WI / NRI"
          value={avgWiNri}
          sub={`${kpis.pdpWells ?? '—'} PDP · ${kpis.pbpCases ?? '—'} PBP`}
          color="var(--teal)"
        />
        <KPICard
          label="Lifting cost"
          value={formatUnitPrice(kpis.liftingCost, '/Mcf')}
          sub="Projected · seller model"
          color="var(--purple)"
        />
      </KPIGrid>

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">Top wells by PV10</div>
            <div className="chart-subtitle">
              {concentration?.boswellPv10Share != null || concentration?.top3Pv10Share != null ? (
                <>
                  {concentration?.concentrationLease || 'Top well'} alone ={' '}
                  {formatPct((concentration?.boswellPv10Share ?? 0) * 100, 0)} of PDP PV10 · top 3 ={' '}
                  {formatPct((concentration?.top3Pv10Share ?? 0) * 100, 0)} · click bar for detail
                </>
              ) : (
                'Click bar for detail'
              )}
            </div>
            <BarChart
              data={topWells.map((w) => ({ ...w, pv10MM: w.pv10M / 1000 }))}
              xKey="lease"
              yKey="pv10MM"
              horizontal
              yFormat={(v) => formatCurrency(v * 1e6)}
              color={(d) => (d.resCat === 'PBP' ? '#14B8A6' : d.lease.includes('BOSWELL') ? '#F59E0B' : '#1B355C')}
              barLabel
              onBarClick={drillWell}
            />
          </div>
          <div className="chart-card">
            <div className="chart-title">Projected cash flow by reserve category</div>
            <div className="chart-subtitle">$MM · Aug 1, 2026 effective · Jun 22 NYMEX strip · click bar for detail</div>
            <StackedBarChart
              data={projectedCf}
              xKey="year"
              series={[
                { key: 'pdp', label: 'PDP', color: '#1B355C' },
                { key: 'pbp', label: 'PBP', color: '#14B8A6' },
              ]}
              yFormat={(v) => (v == null || Number.isNaN(Number(v)) ? '—' : `$${Number(v).toFixed(1)}M`)}
              barLabel
              onBarClick={drillPeriod}
            />
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Highest-priority risks</div>
        <div className="charts-grid charts-grid-3">
          {risks.map((r) => (
            <div key={r.id} className={`risk-card ${r.severity.toLowerCase()}`}>
              <div className="risk-head">
                <div className="risk-title">{r.title}</div>
                <span className={`pill severity-${r.severity.toLowerCase()}`}>{r.severity}</span>
              </div>
              <p className="risk-summary">{r.summary}</p>
              <p className="risk-fix">
                <strong>Next:</strong> {riskNext(r)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="section-title">Reserves summary</div>
        <DataTable
          columns={[
            { key: 'category', label: 'Category' },
            { key: 'wells', label: 'Wells', format: formatInt },
            ...reservesVolumeColumns,
            { key: 'undiscCfMM', label: 'Undisc. CF ($MM)', format: (v) => (v == null ? '—' : `$${v}`) },
            { key: 'pv10MM', label: 'PV10 ($MM)', format: (v) => (v == null ? '—' : `$${v}`) },
            { key: 'pv15MM', label: 'PV15 ($MM)', format: (v) => (v == null ? '—' : `$${v}`) },
            { key: 'pv20MM', label: 'PV20 ($MM)', format: (v) => (v == null ? '—' : `$${v}`) },
          ]}
          data={reservesSummary}
        />
        <div className="chart-actions" style={{ marginTop: 12 }}>
          <button type="button" className="btn-secondary" onClick={() => onNavigate('reserves')}>
            Open Reserves →
          </button>
          <button type="button" className="btn-secondary" onClick={() => onNavigate('diligence')}>
            Diligence checklist →
          </button>
        </div>
      </section>
    </div>
  );
}
